import { useMemo, CSSProperties } from 'react';

type CSSWithVars = CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};
import { useThemeContext } from '@utils/providers/theme-context';

export type ThemeVarKey =
  | 'pageBg' // whole page background
  | 'cardBg' // panels / cards background
  | 'text' // primary text
  | 'icon' // icon color
  | 'border' // generic border
  | 'divide' // dividers
  | 'ring' // focus ring / brand accent
  | 'placeholder';

export type ThemeVars = Record<ThemeVarKey, string>;

export function useThemeVars(): {
  tokens: ThemeVars;
  vars: CSSWithVars;
  cls: {
    pageBg: string;
    cardBg: string;
    text: string;
    border: string;
    divide: string;
  };
} {
  const { theme, getColour, brandColor } = useThemeContext();

  const tokens = useMemo<ThemeVars>(() => {
    const pageBg =
      theme === 'dark' ? getColour('thirdLayer') : getColour('baseLayer');
    const cardBg = getColour('secondLayer');

    const text = getColour('normalText');
    const icon = getColour('icon');
    const ring = brandColor ?? getColour('activeText');

    const border = getColour('border');
    const divide =
      theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';

    const placeholder =
      theme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';

    return { pageBg, cardBg, text, icon, ring, border, divide, placeholder };
  }, [theme, getColour, brandColor]);

  const vars: CSSWithVars = {
    '--page-bg': tokens.pageBg,
    '--card-bg': tokens.cardBg,
    '--text': tokens.text,
    '--icon': tokens.icon,
    '--border': tokens.border,
    '--divide': tokens.divide,
    '--tw-ring-color': tokens.ring,
    '--placeholder-color': tokens.placeholder
  };

  const cls = {
    pageBg: 'bg-[var(--page-bg)]',
    cardBg:
      'bg-[var(--card-bg)] shadow-[0_4px_12px_2px_rgba(0,0,0,0.10),0_2px_4px_rgba(0,0,0,0.06)]',
    text: 'text-[var(--text)]',
    border: 'border-[var(--border)]',
    divide: 'divide-[var(--divide)]'
  };

  return { tokens, vars, cls };
}
